import React, { useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

const MazeGame = React.lazy(() => import('./MazeGame'));

const COUNTDOWN_MS = 60 * 1000;

type MazeToggleProps = {
  className?: string;
  ariaLabel?: string;
};

export default function MazeToggle({
  className = '',
  ariaLabel = 'Lancer le mini-jeu labyrinthe',
}: MazeToggleProps) {
  const [isOpen, setIsOpen] = useState(false);
  const inputId = useId();

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    const previousPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;

    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        return;
      }
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)) {
        event.preventDefault();
      }
    };
    window.addEventListener('keydown', handler);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPaddingRight;
      window.removeEventListener('keydown', handler);
    };
  }, [isOpen]);

  return (
    <>
      <span className={`maze-toggle-shell ${className}`.trim()}>
        <label htmlFor={inputId} className="toggle-container" aria-label={ariaLabel}>
          <input
            id={inputId}
            className="toggle-input"
            type="checkbox"
            checked={isOpen}
            onChange={(event) => setIsOpen(event.target.checked)}
          />
          <div className="toggle-handle-wrapper">
            <div className="toggle-handle">
              <div className="toggle-handle-knob" />
              <div className="toggle-handle-bar-wrapper">
                <div className="toggle-handle-bar" />
              </div>
            </div>
          </div>
          <div className="toggle-base">
            <div className="toggle-base-inside" />
          </div>
        </label>
      </span>

      {isOpen && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm overscroll-contain"
          role="dialog"
          aria-modal="true"
          onWheel={(event) => event.stopPropagation()}
          onTouchMove={(event) => event.stopPropagation()}
        >
          <div className="absolute inset-0" onClick={() => setIsOpen(false)} aria-hidden="true" />
          <div
            className="relative w-full max-w-3xl bg-white rounded-3xl shadow-[0_30px_80px_rgba(0,0,0,0.5)] p-4 sm:p-6 max-h-[92vh] overflow-y-auto overscroll-contain"
            onWheel={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="absolute top-3 right-3 z-10 inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#1a1b24] text-white hover:bg-black transition-colors"
              aria-label="Fermer le mini-jeu"
            >
              <X size={18} strokeWidth={3} />
            </button>
            <React.Suspense fallback={<div className="min-h-[280px] flex items-center justify-center text-sm text-gray-500">Chargement…</div>}>
              <MazeGame countdownMs={COUNTDOWN_MS} />
            </React.Suspense>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
